import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Text, Image as KonvaImage, Line, Arrow, Transformer, Group } from 'react-konva';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

import Konva from 'konva';
import {
  Save,
  Download,
  Trash,
  Type,
  Square,
  Circle as CircleIcon,
  Eye,
  EyeOff,
  Copy,
  ArrowUp,
  ArrowDown,
  ArrowRight,
  Image as ImageIcon,
  Printer,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
} from 'lucide-react';

// Import the utility function
import { canvasToSvg } from '../../lib/konvaToSvg';


// Vector QR Code Component
const VectorQRCode = ({ 
  obj, 
  isSelected, 
  onSelect, 
  onTransform 
}: {
  obj: CanvasObject;
  isSelected: boolean;
  onSelect: (event?: any) => void;
  onTransform: (attrs: any) => void;
}) => {
  if (!obj.qrModules) return null;
  
  const effectiveWidth = (obj.width || 160) * (obj.scaleX || 1);
  const moduleSize = effectiveWidth / obj.qrModules.length;
  const modules = [];
  
  // Create individual rectangles for each QR code module
  for (let row = 0; row < obj.qrModules.length; row++) {
    for (let col = 0; col < obj.qrModules[row].length; col++) {
      if (obj.qrModules[row][col]) {
        modules.push(
          <Rect
            key={`${row}-${col}`}
            x={obj.x + col * moduleSize}
            y={obj.y + row * moduleSize}
            width={moduleSize}
            height={moduleSize}
            fill={obj.fill}
            rotation={obj.rotation}
            listening={false} // Individual modules don't need to listen to events
          />
        );
      }
    }
  }
  
  // Invisible container for selection and transformation
  return (
    <>
      {modules}
      <Rect
        id={obj.id}
        x={obj.x}
        y={obj.y}
        width={obj.width}
        height={obj.height}
        fill="transparent"
        stroke={isSelected ? "#3B82F6" : "transparent"}
        strokeWidth={isSelected ? 1 : 0}
        dash={isSelected ? [5, 5] : []}
        draggable={obj.draggable}
        scaleX={obj.scaleX}
        scaleY={obj.scaleY}
        rotation={obj.rotation}
        onClick={onSelect}
        onDragEnd={(e) => onTransform({
          x: e.target.x(),
          y: e.target.y(),
          scaleX: e.target.scaleX(),
          scaleY: e.target.scaleY(),
          rotation: e.target.rotation(),
          width: e.target.width(),
          height: e.target.height()
        })}
        onTransformEnd={(e) => onTransform({
          x: e.target.x(),
          y: e.target.y(),
          scaleX: e.target.scaleX(),
          scaleY: e.target.scaleY(),
          rotation: e.target.rotation(),
          width: e.target.width(),
          height: e.target.height()
        })}
      />
    </>
  );
};

// Editable Text Component
const EditableText = ({
  obj,
  isSelected,
  onSelect,
  onEdit,
  onTransform,
}: {
  obj: CanvasObject;
  isSelected: boolean;
  onSelect: (event?: any) => void;
  onEdit: (text: string) => void;
  onTransform: (attrs: any) => void;
}) => {
  const groupRef = useRef<Konva.Group>(null);
  const textRef = useRef<Konva.Text>(null);
  const rectRef = useRef<Konva.Rect>(null);
  const [textHeight, setTextHeight] = useState((obj.fontSize || 20) * 1.5);

  // Keep background rect height in sync with rendered text height
  useLayoutEffect(() => {
    if (textRef.current) {
      const h = textRef.current.height();
      if (h > 0) setTextHeight(h);
    }
  });

  const textWidth = obj.width ?? 200;
  const hasBg = obj.textBackground && obj.textBackground !== 'transparent';
  const hasBorder = obj.textBorderColor && obj.textBorderColor !== 'transparent';

  const openEditor = () => {
    const textNode = textRef.current;
    const groupNode = groupRef.current;
    if (!textNode || !groupNode) return;

    textNode.hide();
    if (rectRef.current) rectRef.current.hide();
    groupNode.getLayer()?.batchDraw();

    const stage = groupNode.getStage()!;
    const stageBox = stage.container().getBoundingClientRect();
    // absolutePosition() already accounts for stage scale → gives position in stage CSS pixels
    const stageScale = stage.scaleX(); // Konva internal scale (usually 1 for this canvas)
    const absPos = groupNode.absolutePosition();

    const areaPosition = {
      x: stageBox.left + absPos.x,
      y: stageBox.top + absPos.y,
    };

    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);

    textarea.value = obj.text || '';
    textarea.style.position = 'fixed';
    textarea.style.top = areaPosition.y + 'px';
    textarea.style.left = areaPosition.x + 'px';
    textarea.style.width = (textWidth * stageScale) + 'px';
    textarea.style.minHeight = ((obj.fontSize || 20) * 1.5 * stageScale) + 'px';
    textarea.style.fontSize = ((obj.fontSize || 20) * stageScale) + 'px';
    textarea.style.fontFamily = obj.fontFamily || 'Arial';
    textarea.style.fontStyle = obj.fontStyle?.includes('italic') ? 'italic' : 'normal';
    textarea.style.fontWeight = obj.fontStyle?.includes('bold') ? 'bold' : 'normal';
    textarea.style.textDecoration = obj.textDecoration || 'none';
    textarea.style.textAlign = obj.textAlign || 'left';
    textarea.style.color = obj.fill;
    textarea.style.background = (hasBg ? obj.textBackground! : 'white');
    textarea.style.border = hasBorder
      ? `${obj.textBorderWidth || 1}px solid ${obj.textBorderColor}`
      : '2px solid #3B82F6';
    textarea.style.padding = '2px 4px';
    textarea.style.margin = '0px';
    textarea.style.overflow = 'hidden';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.lineHeight = '1.2';
    textarea.style.transformOrigin = 'left top';
    textarea.style.zIndex = '1000';
    textarea.setAttribute('data-editing', 'true');

    textarea.focus();
    textarea.select();

    const commit = () => {
      if (!textarea.parentNode) return;
      onEdit(textarea.value);
      textarea.parentNode.removeChild(textarea);
      textNode.show();
      if (rectRef.current) rectRef.current.show();
      stage.draw();
    };

    // Guard: ignore blur events fired in the same tick as focus (e.g. from the dblclick)
    let commitEnabled = false;
    setTimeout(() => { commitEnabled = true; }, 150);

    // Auto-grow height
    textarea.addEventListener('input', () => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    });

    textarea.addEventListener('keydown', (e) => {
      e.stopPropagation();
      // Enter adds newline (default behavior); Escape commits without saving
      if (e.key === 'Escape') {
        e.preventDefault();
        if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        textNode.show();
        if (rectRef.current) rectRef.current.show();
        stage.draw();
      }
    });

    textarea.addEventListener('blur', () => { if (commitEnabled) commit(); });
  };

  const handleClick = (e: any) => {
    e.cancelBubble = true; // prevent stage from clearing selection
    onSelect(e);
  };

  return (
    <Group
      ref={groupRef}
      id={obj.id}
      x={obj.x}
      y={obj.y}
      scaleX={obj.scaleX ?? 1}
      scaleY={obj.scaleY ?? 1}
      rotation={obj.rotation ?? 0}
      draggable={obj.draggable}
      onClick={handleClick}
      onDblClick={(e) => { e.cancelBubble = true; openEditor(); }}
      onDragEnd={(e) => onTransform({
        x: e.target.x(),
        y: e.target.y(),
        scaleX: 1,
        scaleY: 1,
        rotation: e.target.rotation(),
        width: textRef.current?.width() ?? obj.width,
        height: obj.height,
      })}
      onTransform={() => {
        // During drag: convert scale into width so text never visually scales
        const grp = groupRef.current;
        if (!grp) return;
        const sx = grp.scaleX();
        if (Math.abs(sx - 1) < 0.001) return;
        const curW = textRef.current ? textRef.current.width() : textWidth;
        const newW = Math.max(50, Math.round(curW * sx));
        if (textRef.current) textRef.current.width(newW);
        if (rectRef.current) {
          rectRef.current.width(newW);
          rectRef.current.height(textRef.current!.height());
        }
        grp.scaleX(1);
        grp.scaleY(1);
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        node.scaleX(1);
        node.scaleY(1);
        const finalWidth = textRef.current?.width() ?? textWidth;
        onTransform({
          x: node.x(),
          y: node.y(),
          width: Math.round(finalWidth),
          scaleX: 1,
          scaleY: 1,
          rotation: node.rotation(),
        });
      }}
    >
      {/* Background / border rect — also acts as the hit area for the Group */}
      <Rect
        ref={rectRef}
        x={0}
        y={0}
        width={textWidth}
        height={textHeight}
        fill={hasBg ? obj.textBackground : 'rgba(0,0,0,0)'}
        stroke={hasBorder ? obj.textBorderColor : 'transparent'}
        strokeWidth={obj.textBorderWidth ?? 1}
      />
      <Text
        ref={textRef}
        x={0}
        y={0}
        text={obj.text}
        fontSize={obj.fontSize ?? 20}
        fontFamily={obj.fontFamily ?? 'Arial'}
        fill={obj.fill}
        width={textWidth}
        wrap="word"
        align={obj.textAlign ?? 'left'}
        fontStyle={obj.fontStyle}
        textDecoration={obj.textDecoration}
        listening={false}
      />
    </Group>
  );
};

// Confirmation Dialog Component
// Removed unused ConfirmationDialog component

// Types

interface StickerDesign {
  id: string;
  name: string;
  design_data: any;
  format: string;
  width: number;
  height: number;
  is_template: boolean;
  template_name?: string;
  preview_url?: string;
  print_url?: string;
  form_id: string;
}

interface CanvasObject {
  id: string;
  type: 'text' | 'rectangle' | 'circle' | 'qr-code' | 'image' | 'line' | 'polygon' | 'group' | 'arrow';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  text?: string;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  strokeScaleEnabled?: boolean;
  fontSize?: number;
  fontFamily?: string;
  image?: HTMLImageElement;
  imageData?: string;
  formId?: string;
  draggable: boolean;
  scaleX?: number;
  scaleY?: number;
  rotation?: number;
  points?: number[];
  cornerRadius?: number;
  qrData?: string;
  qrModules?: boolean[][];
  layerType?: 'artwork' | 'spot_white' | 'die_cut' | 'kiss_cut';
  children?: CanvasObject[];
  fontStyle?: string;
  textDecoration?: string;
  textAlign?: 'left' | 'center' | 'right';
  textBackground?: string;
  textBorderColor?: string;
  textBorderWidth?: number;
  pointerLength?: number;
  pointerWidth?: number;
  tension?: number;
  fillLinearGradientStartPoint?: { x: number; y: number };
  fillLinearGradientEndPoint?: { x: number; y: number };
  fillRadialGradientStartPoint?: { x: number; y: number };
  fillRadialGradientEndPoint?: { x: number; y: number };
  fillRadialGradientStartRadius?: number;
  fillRadialGradientEndRadius?: number;
  fillLinearGradientColorStops?: number[];
  fillRadialGradientColorStops?: number[];
  fillPriority?: string;
}



interface CanvasDimensions {
  totalWidthMm: number;
  totalHeightMm: number;
  designWidthMm: number;
  designHeightMm: number;
  totalWidthPx: number;
  totalHeightPx: number;
  designWidthPx: number;
  designHeightPx: number;
  width: number;  // For backward compatibility
  height: number; // For backward compatibility
}

// StickerDesigner Component - Restored from 0213eac
// Features:
// - QR code generation with feedback form integration
// - Shape tools with customization
// - Text editing with font selection
// - Image upload and manipulation
// - Export with proper color handling
// Last updated: 2024-03-20
// Added deployment-ready changes and fixed TypeScript errors

const StickerDesigner = (): JSX.Element => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { isTrialExpired, isSubscribed } = useSubscription();
  
  // Refs
  const stageRef = useRef<Konva.Stage>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  
  // State
  const [forms, setForms] = useState<Array<{ id: string; name: string; is_active: boolean }>>([]);
  // Removed unused state variables and functions
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<string>('square');
  const [designName, setDesignName] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [designs, setDesigns] = useState<StickerDesign[]>([]);
  const [loadingDesigns, setLoadingDesigns] = useState<boolean>(false);
  const [showGrid, setShowGrid] = useState(true);
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null);
  const [selectedObjectIds, setSelectedObjectIds] = useState<string[]>([]);
  const [canvasObjects, setCanvasObjects] = useState<CanvasObject[]>([]);
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState('#ffffff');
  const [currentDesignId, setCurrentDesignId] = useState<string | null>(null);
  const [exportingForPrint, setExportingForPrint] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Canvas size controls in mm and pixels (DPI: 300 for print quality)
  const [canvasWidthMm, setCanvasWidthMm] = useState<number>(50);
  const [canvasHeightMm, setCanvasHeightMm] = useState<number>(50);
  const [measurementUnit, setMeasurementUnit] = useState<'mm' | 'inch'>('mm');
  const DPI = 300; // Dots per inch for print quality
  const MM_TO_INCH = 0.0393701; // Convert mm to inches
  const BLEED_SIZE_MM = 2;
  // Font size helpers: 1pt = 1/72 inch; at 300 DPI: 1pt = 300/72 ≈ 4.167 canvas px
  const PT_TO_PX = DPI / 72;
  const pxToPt = (px: number) => Math.round(px / PT_TO_PX);
  const ptToPx = (pt: number) => Math.round(pt * PT_TO_PX);

  // Calculate initial dimensions
  const calculateDimensions = (widthMm: number, heightMm: number): CanvasDimensions => {
    const designWidthPx = Math.round(widthMm * MM_TO_INCH * DPI);
    const designHeightPx = Math.round(heightMm * MM_TO_INCH * DPI);
    const bleedPx = Math.round(BLEED_SIZE_MM * MM_TO_INCH * DPI);
    
    return {
      totalWidthMm: widthMm + (BLEED_SIZE_MM * 2),
      totalHeightMm: heightMm + (BLEED_SIZE_MM * 2),
      designWidthMm: widthMm,
      designHeightMm: heightMm,
      totalWidthPx: designWidthPx + (bleedPx * 2),
      totalHeightPx: designHeightPx + (bleedPx * 2),
      designWidthPx,
      designHeightPx,
      width: designWidthPx,
      height: designHeightPx
    };
  };

  const [canvasDimensions, setCanvasDimensions] = useState<CanvasDimensions>(
    calculateDimensions(canvasWidthMm, canvasHeightMm)
  );

  // Update dimensions when size changes
  useEffect(() => {
    setCanvasDimensions(calculateDimensions(canvasWidthMm, canvasHeightMm));
  }, [canvasWidthMm, canvasHeightMm]);

  // Load forms when component mounts
  useEffect(() => {
    if (user) {
      fetchForms();
    }
  }, [user]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Removed unused delete confirmation state

  // Export-related state
  // Removed unused export dialog state
  // Removed unused export settings state
  
  // Font options
  const fontOptions = [
    'Arial',
    'Times New Roman',
    'Helvetica',
    'Roboto',
    'Georgia',
    'Courier New',
    'Verdana',
    'Comic Sans MS',
    'Impact',
    'Trebuchet MS'
  ];
  
  // Removed unused color options
  
  // Canvas dimensions are managed by state

  // Helper function to convert pixels back to mm for display
  const pixelsToMm = (pixels: number) => Math.round(pixels / (MM_TO_INCH * DPI));
  
  // Helper functions for unit conversions
  const mmToInch = (mm: number) => parseFloat((mm * MM_TO_INCH).toFixed(3));
  const inchToMm = (inch: number) => Math.round(inch / MM_TO_INCH);
  
  // Get display values based on current unit
  const getDisplayWidth = () => measurementUnit === 'mm' ? canvasWidthMm : mmToInch(canvasWidthMm);
  const getDisplayHeight = () => measurementUnit === 'mm' ? canvasHeightMm : mmToInch(canvasHeightMm);
  
  // Handle input changes with unit conversion
  const handleWidthChange = (value: number) => {
    if (measurementUnit === 'mm') {
      setCanvasWidthMm(Math.max(10, value));
    } else {
      setCanvasWidthMm(Math.max(10, inchToMm(value)));
    }
  };
  
  const handleHeightChange = (value: number) => {
    if (measurementUnit === 'mm') {
      setCanvasHeightMm(Math.max(10, value));
    } else {
      setCanvasHeightMm(Math.max(10, inchToMm(value)));
    }
  };

  // Fetch forms when component mounts
  useEffect(() => {
    if (user) {
    fetchForms();
    }
  }, [user]);

  // Fetch user designs
  useEffect(() => {
    if (user) {
      console.log('Fetching designs for user:', user.id);
      fetchDesigns();
    }
  }, [user]);

  // Debug: Log when designs state changes
  useEffect(() => {
    console.log('Designs state changed:', designs);
  }, [designs]);

  // Add keyboard event handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle events when not editing text and not in a textarea/input
      const isInTextArea = document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT';
      
      if (isInTextArea) return;

      if (selectedObjectId) {
        const moveDistance = e.shiftKey ? 10 : 1;
        const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
        
        if (selectedObject) {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              updateObjectPosition(selectedObjectId, selectedObject.x - moveDistance, selectedObject.y);
              break;
            case 'ArrowRight':
              e.preventDefault();
              updateObjectPosition(selectedObjectId, selectedObject.x + moveDistance, selectedObject.y);
              break;
            case 'ArrowUp':
              e.preventDefault();
              updateObjectPosition(selectedObjectId, selectedObject.x, selectedObject.y - moveDistance);
              break;
            case 'ArrowDown':
              e.preventDefault();
              updateObjectPosition(selectedObjectId, selectedObject.x, selectedObject.y + moveDistance);
              break;
            case 'Delete':
            case 'Backspace':
        e.preventDefault();
        deleteSelected();
              break;
            case 'Escape':
        setSelectedObjectId(null);
        setIsEditing(false);
              break;
            case 'g':
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                groupSelected();
              }
              break;
            case 'u':
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                ungroupSelected();
              }
              break;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedObjectId, isEditing, canvasObjects]);

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const nodes = selectedObjectIds
        .map(id => stageRef.current?.findOne(`#${id}`))
        .filter(node => node !== undefined);
      
      if (nodes.length > 0) {
        transformerRef.current.nodes(nodes);
      } else {
        transformerRef.current.nodes([]);
      }
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedObjectIds]);

  // Removed unused export settings effect

  // Fetch forms from the database
  const fetchForms = async () => {
    try {
      const { data, error } = await supabase
        .from('forms')
        .select('id, name, is_active')
        .eq('user_id', user?.id);

      if (error) throw error;

      // If trial is expired and not subscribed, mark all forms as inactive
      const processedForms = (data || []).map(form => ({
        ...form,
        is_active: isTrialExpired && !isSubscribed ? false : form.is_active
      }));

      setForms(processedForms);
      if (processedForms.length > 0) {
        setSelectedForm(processedForms[0].id);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      addToast('Failed to fetch forms', 'error');
    }
  };

  // Fetch user designs
  const fetchDesigns = async () => {
    try {
      setLoadingDesigns(true);
      console.log('Starting fetchDesigns...', { userId: user?.id });
      const { data, error } = await supabase
        .from('sticker_designs')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Fetched designs:', data);
      setDesigns(data || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
      addToast('Failed to fetch designs', 'error');
    } finally {
      setLoadingDesigns(false);
    }
  };

  const generateGridLines = () => {
    if (!showGrid) return [];
    
    const dimensions = canvasDimensions;
    const gridSize = 20;
    const lines = [];
    
    // Vertical lines
    for (let i = 0; i <= dimensions.totalWidthPx; i += gridSize) {
      lines.push(
        <Line
          key={`v-${i}`}
          points={[i, 0, i, dimensions.totalHeightPx]}
          stroke="#e5e7eb"
          strokeWidth={1}
          listening={false}
        />
      );
    }
    
    // Horizontal lines
    for (let i = 0; i <= dimensions.totalHeightPx; i += gridSize) {
      lines.push(
        <Line
          key={`h-${i}`}
          points={[0, i, dimensions.totalWidthPx, i]}
          stroke="#e5e7eb"
          strokeWidth={1}
          listening={false}
        />
      );
    }
    
    return lines;
  };

  // Helper function to generate QR code matrix data
  const generateQRMatrix = async (data: string): Promise<boolean[][]> => {
    try {
      // Use qrcode library to generate the module matrix
      const QRCodeLib = (await import('qrcode')).default;
      const qr = QRCodeLib.create(data, { errorCorrectionLevel: 'H' });
      
      // Convert the modules data to a 2D boolean array
      const size = qr.modules.size;
      const matrix: boolean[][] = [];
      
      for (let row = 0; row < size; row++) {
        matrix[row] = [];
        for (let col = 0; col < size; col++) {
          matrix[row][col] = qr.modules.get(row, col) === 1;
        }
      }
      
      return matrix;
    } catch (error) {
      throw error;
    }
  };

  const handleFormSelect = async (formId: string) => {
    setSelectedForm(formId);
    
    if (formId) {
      try {
        // Generate QR code for the selected form
        const qrCodeUrl = `${window.location.origin}/feedback/${formId}`;
        
        // Generate QR code matrix for vector rendering
        const qrMatrix = await generateQRMatrix(qrCodeUrl);
        
        // Remove existing QR codes
        setCanvasObjects(prev => prev.filter(obj => obj.type !== 'qr-code'));
        
        // Create vector QR code object
        const newQRCode: CanvasObject = {
          id: `qr-${Date.now()}`,
          type: 'qr-code',
          x: 50,
          y: 50,
          width: 160,
          height: 160,
          fill: '#000000',
          qrData: qrCodeUrl,
          qrModules: qrMatrix,
          formId: formId, // Store the form ID
          draggable: true,
          scaleX: 1,
          scaleY: 1,
          rotation: 0
        };
        
        setCanvasObjects(prev => [...prev, newQRCode]);
        addToast('QR code added to canvas', 'success');
        
      } catch (error) {
        addToast('Failed to add QR code', 'error');
      }
    }
  };

  const addText = () => {
    const newText: CanvasObject = {
      id: `text-${Date.now()}`,
      type: 'text',
      x: 100,
      y: 100,
      width: 200,
      text: 'Tekst',
      fontSize: ptToPx(10),
      fontFamily: 'Arial',
      fill: '#000000',
      textAlign: 'left',
      textBackground: 'transparent',
      textBorderColor: 'transparent',
      textBorderWidth: 1,
      draggable: true,
      scaleX: 1,
      scaleY: 1,
      rotation: 0
    };
    
    setCanvasObjects(prev => [...prev, newText]);
    // Clear existing selection and select only the new text
    setSelectedObjectIds([newText.id]);
    setSelectedObjectId(newText.id);
    addToast('Text added to canvas', 'success');
  };

  const addFullCanvasBackground = () => {
    // If an object is selected, resize it to cover full canvas
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId 
            ? { 
                ...obj, 
                x: 0,
                y: 0,
                width: canvasDimensions.totalWidthPx,
                height: canvasDimensions.totalHeightPx,
                scaleX: 1,
                scaleY: 1,
                rotation: 0
              }
            : obj
        )
      );
      addToast('Object resized to cover full canvas', 'success');
      return;
    }

    // If no object is selected, create a new background
    const newBackground: CanvasObject = {
      id: generateId(),
      type: 'rectangle',
      x: 0,
      y: 0,
      width: canvasDimensions.totalWidthPx,
      height: canvasDimensions.totalHeightPx,
      fill: '#ffffff',
      stroke: 'transparent',
      strokeWidth: 0,
      cornerRadius: 0,
      strokeScaleEnabled: false,
      draggable: true,
      scaleX: 1,
      scaleY: 1,
      rotation: 0
    };

    // Add background as the first object (bottom layer)
    setCanvasObjects(prev => [newBackground, ...prev]);
    // Clear existing selection and select only the new background
    setSelectedObjectIds([newBackground.id]);
    setSelectedObjectId(newBackground.id);
    addToast('Full canvas background added', 'success');
  };

  const addShape = (shapeType: string) => {
    const stage = stageRef.current;
    if (!stage) return;

    const baseProps = {
      id: generateId(),
      x: stage.width() / 2,
      y: stage.height() / 2,
      draggable: true,
      scaleX: 1,
      scaleY: 1,
      rotation: 0
    };

    let newShape: CanvasObject;

    switch (shapeType) {
      case 'rectangle':
        newShape = {
          ...baseProps,
          type: 'rectangle',
          width: 100,
          height: 60,
          fill: '#3B82F6',
          stroke: '#000000',
          strokeWidth: 2,
          cornerRadius: 0,
          strokeScaleEnabled: false
        };
        break;
      case 'square':
        newShape = {
          ...baseProps,
          type: 'rectangle',
          width: 80,
          height: 80,
          fill: '#8B5CF6',
          stroke: '#000000',
          strokeWidth: 2,
          cornerRadius: 0,
          strokeScaleEnabled: false
        };
        break;
      case 'circle':
        newShape = {
          ...baseProps,
          type: 'circle',
          radius: 50,
          fill: '#10B981',
          stroke: '#000000',
          strokeWidth: 2,
          strokeScaleEnabled: false
        };
        break;
      case 'polygon':
        // Create a regular pentagon
        const radius = 40;
        const points: number[] = [];
        const sides = 5;
        for (let i = 0; i < sides; i++) {
          const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
          points.push(
            radius * Math.cos(angle),
            radius * Math.sin(angle)
          );
        }
        newShape = {
          ...baseProps,
          type: 'polygon',
          points,
          fill: '#60A5FA',
          fillLinearGradientStartPoint: { x: -radius, y: -radius },
          fillLinearGradientEndPoint: { x: radius, y: radius },
          fillLinearGradientColorStops: [0, 0.5, 1],
          stroke: '#000000',
          strokeWidth: 2,
          strokeScaleEnabled: false
        };
        break;
      default:
        return;
    }

    setCanvasObjects([...canvasObjects, newShape]);
    // Clear existing selection and select only the new shape
    setSelectedObjectIds([newShape.id]);
    setSelectedObjectId(newShape.id);
  };

  // Add gradient support to existing objects
  // Gradient functionality removed

  const addImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      addToast('Please select a valid image file', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const dimensions = canvasDimensions;
        const maxSize = Math.min(dimensions.width, dimensions.height) * 0.3;
        
        // Calculate aspect ratio to maintain proportions
        const aspectRatio = img.width / img.height;
        let newWidth = maxSize;
        let newHeight = maxSize;
        
        if (aspectRatio > 1) {
          newHeight = maxSize / aspectRatio;
        } else {
          newWidth = maxSize * aspectRatio;
        }

        const newImage: CanvasObject = {
          id: `image-${Date.now()}`,
          type: 'image',
          x: dimensions.width / 2 - newWidth / 2,
          y: dimensions.height / 2 - newHeight / 2,
          width: newWidth,
          height: newHeight,
          fill: '#000000',
          image: img,
          imageData: e.target?.result as string,
          draggable: true,
          scaleX: 1,
          scaleY: 1,
          rotation: 0
        };

        setCanvasObjects(prev => [...prev, newImage]);
        setSelectedObjectId(newImage.id);

      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    event.target.value = '';
  };

  const addHorizontalLine = (thickness: number = 2) => {
    const dimensions = canvasDimensions;
    const lineLength = dimensions.width * 0.3; // 30% of canvas width
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    
    const newLine: CanvasObject = {
      id: `line-${Date.now()}`,
      type: 'line',
      x: 0,
      y: 0,
      points: [centerX - lineLength/2, centerY, centerX + lineLength/2, centerY],
      fill: '#000000',
      stroke: '#000000',
      strokeWidth: thickness,
      draggable: true,
      scaleX: 1,
      scaleY: 1,
      rotation: 0
    };

    setCanvasObjects(prev => [...prev, newLine]);
    setSelectedObjectId(newLine.id);

  };

  const changeStrokeColor = (color: string) => {
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId 
            ? { ...obj, stroke: color }
            : obj
        )
      );

    }
  };

  const changeStrokeWidth = (width: number) => {
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId 
            ? { ...obj, strokeWidth: width }
            : obj
        )
      );

    }
  };

  const deleteSelected = () => {
    if (selectedObjectId) {
      setCanvasObjects(prev => prev.filter(obj => obj.id !== selectedObjectId));
      setSelectedObjectId(null);

    }
  };

  const duplicateSelected = () => {
    if (selectedObjectId) {
      const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
      if (selectedObject) {
        let newObject = {
          ...selectedObject,
          id: `${selectedObject.type}-${Date.now()}`,
          x: selectedObject.x + 20,
          y: selectedObject.y + 20
        };

        // For QR codes and images, recreate the image from imageData
        if ((selectedObject.type === 'qr-code' || selectedObject.type === 'image') && selectedObject.imageData) {
          const img = new Image();
          img.src = selectedObject.imageData;
          newObject = { ...newObject, image: img };
        }
        
        setCanvasObjects(prev => [...prev, newObject]);
        setSelectedObjectId(newObject.id);

      }
    }
  };

  const bringToFront = () => {
    if (selectedObjectId) {
      setCanvasObjects(prev => {
        const selectedObject = prev.find(obj => obj.id === selectedObjectId);
        if (selectedObject) {
          const otherObjects = prev.filter(obj => obj.id !== selectedObjectId);
          return [...otherObjects, selectedObject];
        }
        return prev;
      });

    }
  };

  const sendToBack = () => {
    if (selectedObjectId) {
      setCanvasObjects(prev => {
        const selectedObject = prev.find(obj => obj.id === selectedObjectId);
        if (selectedObject) {
          const otherObjects = prev.filter(obj => obj.id !== selectedObjectId);
          return [selectedObject, ...otherObjects];
        }
        return prev;
      });

    }
  };

  const changeObjectColor = (color: string) => {
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId 
            ? { ...obj, fill: color === 'transparent' ? 'rgba(0,0,0,0)' : color }
            : obj
        )
      );
    }
  };

  const changeFontFamily = (fontFamily: string) => {
    if (selectedObjectId) {
      setCanvasObjects(prev =>
        prev.map(obj =>
          obj.id === selectedObjectId && (obj.type === 'text')
            ? { ...obj, fontFamily }
            : obj
        )
      );

    }
  };

  const changeFontSize = (pt: number) => {
    if (!selectedObjectId || isNaN(pt) || pt < 1) return;
    const fontSize = ptToPx(pt);
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === selectedObjectId && obj.type === 'text'
          ? { ...obj, fontSize, scaleX: 1, scaleY: 1 }
          : obj
      )
    );
  };

  const changeTextAlign = (textAlign: 'left' | 'center' | 'right') => {
    if (!selectedObjectId) return;
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === selectedObjectId && obj.type === 'text' ? { ...obj, textAlign } : obj
      )
    );
  };

  const changeTextBackground = (color: string) => {
    if (!selectedObjectId) return;
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === selectedObjectId && obj.type === 'text' ? { ...obj, textBackground: color } : obj
      )
    );
  };

  const changeTextBorderColor = (color: string) => {
    if (!selectedObjectId) return;
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === selectedObjectId && obj.type === 'text' ? { ...obj, textBorderColor: color } : obj
      )
    );
  };

  const changeTextBorderWidth = (width: number) => {
    if (!selectedObjectId) return;
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === selectedObjectId && obj.type === 'text' ? { ...obj, textBorderWidth: width } : obj
      )
    );
  };

  const updateObjectPosition = (id: string, x: number, y: number) => {
    setCanvasObjects(prev => 
      prev.map(obj => 
        obj.id === id 
          ? { ...obj, x, y }
          : obj
      )
    );
  };

  const updateObjectTransform = (id: string, attrs: any) => {
    setCanvasObjects(prev =>
      prev.map(obj =>
        obj.id === id
          ? {
              ...obj,
              x: attrs.x,
              y: attrs.y,
              scaleX: attrs.scaleX,
              scaleY: attrs.scaleY,
              rotation: attrs.rotation,
              width: attrs.width,
              height: attrs.height,
              ...(attrs.fontSize !== undefined && { fontSize: attrs.fontSize }),
            }
          : obj
      )
    );
  };

  const handleTextEdit = (id: string, newText: string) => {
    setCanvasObjects(prev => 
      prev.map(obj => 
        obj.id === id && obj.type === 'text'
          ? { ...obj, text: newText }
          : obj
      )
    );
    setIsEditing(false);
  };

  const saveDesign = async () => {
    if (!designName.trim()) {
      addToast('Please enter a design name', 'error');
      return;
    }

    setSaving(true);
    
    try {
      // Check if design name already exists for this user
      const { data: existingDesigns, error: checkError } = await supabase
        .from('sticker_designs')
        .select('id')
        .eq('user_id', user?.id)
        .eq('name', designName)
        .eq('is_template', false);

      if (checkError) throw checkError;

      if (existingDesigns && existingDesigns.length > 0) {
        addToast('A design with this name already exists. Please choose a different name.', 'error');
        return;
      }

      await withoutSelectionAndGrid(async () => {
        const stage = stageRef.current;
        if (!stage) throw new Error('Canvas not found');

        // Generate preview
        const dataURL = stage.toDataURL({
          mimeType: 'image/png',
          quality: 1,
          pixelRatio: 1
        });

        const designData = {
          objects: canvasObjects,
          backgroundColor: canvasBackgroundColor,
          format: selectedFormat,
          dimensions: canvasDimensions,
          dimensionsMm: { width: canvasWidthMm, height: canvasHeightMm },
          measurementUnit: measurementUnit
        };

        const { data: insertedDesign, error } = await supabase
          .from('sticker_designs')
          .insert({
            user_id: user?.id,
            name: designName,
            design_data: designData,
            format: selectedFormat,
            width: canvasDimensions.width,
            height: canvasDimensions.height,
            is_template: false,
            template_name: null,
            preview_url: dataURL,
            form_id: selectedForm
          })
          .select('id')
          .single();

        if (error) throw error;
        if (insertedDesign) setCurrentDesignId(insertedDesign.id);

        addToast('Design saved successfully!', 'success');
        await fetchDesigns();
        
        // Clear design name for next design
        setDesignName('');
      });
    } catch (error) {
      addToast('Failed to save design', 'error');
    } finally {
      setSaving(false);
    }
  };

  const loadDesign = (design: StickerDesign) => {
    // Clear selections
    setSelectedObjectId(null);
    setSelectedObjectIds([]);
    
    // Set format and form
    setSelectedFormat(design.format);
    setSelectedForm(design.form_id);
    setDesignName(design.name);
    setCurrentDesignId(design.id);

    // Set canvas dimensions from the loaded design
    if (design.design_data?.dimensionsMm) {
      setCanvasWidthMm(design.design_data.dimensionsMm.width);
      setCanvasHeightMm(design.design_data.dimensionsMm.height);
    } else {
      // Fallback: calculate mm from pixels if old format
      const widthMm = pixelsToMm(design.width);
      const heightMm = pixelsToMm(design.height);
      setCanvasWidthMm(widthMm);
      setCanvasHeightMm(heightMm);
    }

    // Restore measurement unit preference if available
    if (design.design_data?.measurementUnit) {
      setMeasurementUnit(design.design_data.measurementUnit);
    }

    // Set background color if available
    if (design.design_data?.backgroundColor) {
      setCanvasBackgroundColor(design.design_data.backgroundColor);
    }

    // Load design data and restore images for QR codes
    if (design.design_data?.objects) {
      console.log('Loading design objects:', design.design_data.objects);
      
      const restoreObjects = async () => {
        const restoredObjects = await Promise.all(
          design.design_data.objects.map(async (obj: CanvasObject) => {
            // Ensure all objects have proper draggable property
            const baseObj = {
              ...obj,
              draggable: obj.draggable !== false, // Default to true unless explicitly false
            };
            
            console.log('Processing object:', obj.type, obj.id, 'draggable:', baseObj.draggable);
            
            if (obj.type === 'qr-code') {
              // Check if this is a new vector QR code or old image-based QR code
              if (obj.qrModules && obj.qrData) {
                // New vector QR code - use as is
                console.log('Loading vector QR code:', obj.id);
                return baseObj;
              } else if (obj.imageData && !obj.image && obj.formId) {
                // Old image-based QR code - convert to vector
                try {
                  console.log('Converting old QR code to vector:', obj.id);
                  const qrCodeUrl = obj.qrData || `${window.location.origin}/feedback/${obj.formId}`;
                  const qrMatrix = await generateQRMatrix(qrCodeUrl);
                  return {
                    ...baseObj,
                    qrData: qrCodeUrl,
                    qrModules: qrMatrix,
                    // Remove old image properties
                    image: undefined,
                    imageData: undefined
                  };
                } catch (error) {
                  console.error('Failed to convert QR code, using fallback:', error);
                  // Fallback to image-based if conversion fails
                  const img = new Image();
                  img.crossOrigin = 'anonymous';
                  
                  return new Promise((resolve) => {
                    img.onload = () => {
                      console.log('QR code fallback image loaded:', obj.id);
                      resolve({ ...baseObj, image: img });
                    };
                    img.onerror = (error) => {
                      console.error('QR code fallback image failed to load:', obj.id, error);
                      resolve(baseObj);
                    };
                    img.src = obj.imageData || '';
                  });
                }
              }
            } else if (obj.type === 'image' && obj.imageData && !obj.image) {
              // Regular images - restore from base64 data
              console.log('Loading image object:', obj.id);
              const img = new Image();
              img.crossOrigin = 'anonymous';
              
              return new Promise((resolve) => {
                img.onload = () => {
                  console.log('Image loaded successfully:', obj.id, img.width, img.height);
                  resolve({ ...baseObj, image: img });
                };
                img.onerror = (error) => {
                  console.error('Image failed to load:', obj.id, error);
                  // Return object without image if loading fails
                  resolve(baseObj);
                };
                img.src = obj.imageData || '';
              });
            }
            
            console.log('Loading regular object:', obj.type, obj.id);
            return baseObj;
          })
        );
        
        console.log('All objects restored:', restoredObjects);
        setCanvasObjects(restoredObjects);
        
        // Force multiple re-renders to ensure images are displayed
        setTimeout(() => {
          if (stageRef.current) {
            stageRef.current.batchDraw();
          }
          // Second re-render after a longer delay for any slow-loading images
          setTimeout(() => {
            if (stageRef.current) {
              stageRef.current.batchDraw();
            }
          }, 500);
        }, 100);
      };
      
      restoreObjects().catch(error => {
        console.error('Error restoring objects:', error);
        addToast('Some objects may not have loaded correctly', 'warning');
      });
    } else {
      console.log('No objects to load');
      setCanvasObjects([]);
    }
  };

  const handleDeleteDesign = async (designId: string, designName: string) => {
    if (confirm(`Are you sure you want to delete "${designName}"?`)) {
      try {
        const { error } = await supabase
          .from('sticker_designs')
          .delete()
          .eq('id', designId)
          .eq('user_id', user?.id);

        if (error) throw error;

        addToast('Design deleted successfully', 'success');
        fetchDesigns();
      } catch (error) {
        console.error('Error deleting design:', error);
        addToast('Failed to delete design', 'error');
      }
    }
  };

  const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);

  // Add function to change corner radius for rectangles
  const changeCornerRadius = (radius: number) => {
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId && obj.type === 'rectangle'
            ? { ...obj, cornerRadius: radius }
            : obj
        )
      );
    }
  };

  // Add new function for changing text color specifically
  const changeTextColor = (color: string) => {
    if (selectedObjectId) {
      setCanvasObjects(prev => 
        prev.map(obj => 
          obj.id === selectedObjectId && obj.type === 'text'
            ? { ...obj, fill: color }
            : obj
        )
      );
    }
  };

  // Helper function to manage selection and grid state during export/save operations
  const withoutSelectionAndGrid = async (operation: () => Promise<void> | void) => {
    // Store current state
    const previousSelectedObjectId = selectedObjectId;
    const previousSelectedObjectIds = selectedObjectIds;
    const previousShowGrid = showGrid;

    // Clear selection and hide grid
    setSelectedObjectId(null);
    setSelectedObjectIds([]);
    setShowGrid(false);

    // Wait for state updates to be reflected
    await new Promise(resolve => setTimeout(resolve, 50));

    try {
      // Perform the operation
      await operation();
    } finally {
      // Restore previous state
      setSelectedObjectId(previousSelectedObjectId);
      setSelectedObjectIds(previousSelectedObjectIds);
      setShowGrid(previousShowGrid);
    }
  };

  const generateId = () => `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const [canvasRadius, setCanvasRadius] = useState(0);
  const [exportFormat, setExportFormat] = useState<'png' | 'pdf'>('png');
  const [exportQuality, setExportQuality] = useState(2); // Scale factor for PNG export

  // Function to export the design as PNG
  const exportAsPNG = async () => {
    if (!stageRef.current) return;

    await withoutSelectionAndGrid(async () => {
      const stage = stageRef.current;
      if (!stage) return;

      const dataURL = stage.toDataURL({
        pixelRatio: exportQuality, // Higher quality export
        mimeType: 'image/png'
      });

      // Create download link
      const link = document.createElement('a');
      link.download = `${designName || 'sticker-design'}.png`;
      link.href = dataURL;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  // Function to export the design as PDF
  const exportAsPDF = async () => {
    if (!stageRef.current) return;
    
    try {
      await withoutSelectionAndGrid(async () => {
        const stage = stageRef.current;
        if (!stage) return;

        const dataURL = stage.toDataURL({
          pixelRatio: 2,
          mimeType: 'image/png'
        });

        // Create a new jsPDF instance
        const { jsPDF } = await import('jspdf');
        const pdf = new jsPDF({
          orientation: canvasWidthMm > canvasHeightMm ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [canvasWidthMm, canvasHeightMm]
        });

        // Add the image to the PDF
        pdf.addImage(
          dataURL,
          'PNG',
          0,
          0,
          canvasWidthMm,
          canvasHeightMm
        );

        // Save the PDF
        pdf.save(`${designName || 'sticker-design'}.pdf`);
      });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      addToast('Failed to generate PDF. Please try again.', 'error');
    }
  };

  // Export design as StickerIT-compliant print file (PDF) + proof (PNG)
  const exportForPrint = async () => {
    if (!user) return;

    if (!currentDesignId) {
      addToast('Sla het design eerst op voordat je het exporteert voor print.', 'error');
      return;
    }

    setExportingForPrint(true);
    try {
      // Generate both SVG (for PDF) and proof PNG from stage simultaneously
      let proofPng: string | null = null;
      let svg = '';

      await withoutSelectionAndGrid(async () => {
        const stage = stageRef.current;
        if (stage) {
          proofPng = stage.toDataURL({ pixelRatio: 1, mimeType: 'image/png' });
        }
        svg = canvasToSvg(
          canvasObjects,
          canvasBackgroundColor,
          canvasDimensions.totalWidthPx,
          canvasDimensions.totalHeightPx
        );
      });

      const response = await fetch('/api/generate-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          svg,
          width: canvasDimensions.totalWidthMm,
          height: canvasDimensions.totalHeightMm,
          designId: currentDesignId,
          userId: user.id,
          proofPng,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `API error ${response.status}`);
      }

      const { url, proofUrl } = await response.json();
      addToast(`Print file gegenereerd!${proofUrl ? ' Proof PNG opgeslagen.' : ''}`, 'success');

      // Open PDF in new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Print export failed:', error);
      addToast('Print export mislukt. Probeer opnieuw.', 'error');
    } finally {
      setExportingForPrint(false);
    }
  };

  // Function to handle file export
  const handleFileExport = async () => {
    if (exportFormat === 'png') {
      await exportAsPNG();
    } else {
      await exportAsPDF();
    }
  };

  // Add group functionality
  const groupSelected = () => {
    if (selectedObjectIds.length < 2) return;

    const selectedObjects = canvasObjects.filter(obj => selectedObjectIds.includes(obj.id));
    if (selectedObjects.length < 2) return;

    // Calculate group bounds
    const bounds = selectedObjects.reduce((acc, obj) => {
      const x = obj.x;
      const y = obj.y;
      const width = obj.width || (obj.type === 'circle' ? obj.radius! * 2 : 0);
      const height = obj.height || (obj.type === 'circle' ? obj.radius! * 2 : 0);

      return {
        x: Math.min(acc.x, x),
        y: Math.min(acc.y, y),
        width: Math.max(acc.width, x + width),
        height: Math.max(acc.height, y + height)
      };
    }, { x: Infinity, y: Infinity, width: -Infinity, height: -Infinity });

    const groupId = `group-${Date.now()}`;
    const group: CanvasObject = {
      id: groupId,
      type: 'group',
      x: bounds.x,
      y: bounds.y,
      width: bounds.width - bounds.x,
      height: bounds.height - bounds.y,
      fill: 'transparent',
      draggable: true,
      children: selectedObjects
    };

    // Remove selected objects and add group
    setCanvasObjects(prev => [
      ...prev.filter(obj => !selectedObjectIds.includes(obj.id)),
      group
    ]);
    setSelectedObjectId(groupId);
    setSelectedObjectIds([groupId]);
  };

  const ungroupSelected = () => {
    if (!selectedObjectId) return;
    
    const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
    if (!selectedObject || selectedObject.type !== 'group' || !selectedObject.children) return;

    // Add all children back to canvas
    setCanvasObjects(prev => [
      ...prev.filter(obj => obj.id !== selectedObjectId),
      ...(selectedObject.children || [])
    ]);
    setSelectedObjectId(null);
  };

  const alignObjects = (direction: 'left' | 'center-h' | 'right' | 'top' | 'center-v' | 'bottom') => {
    const ids = selectedObjectIds.length > 1 ? selectedObjectIds : selectedObjectId ? [selectedObjectId] : [];
    if (ids.length === 0) return;

    const cw = canvasDimensions.totalWidthPx;
    const ch = canvasDimensions.totalHeightPx;

    setCanvasObjects(prev =>
      prev.map(obj => {
        if (!ids.includes(obj.id)) return obj;
        const w = (obj.width ?? (obj.radius ? obj.radius * 2 : 0)) * (obj.scaleX ?? 1);
        const h = (obj.height ?? (obj.radius ? obj.radius * 2 : 0)) * (obj.scaleY ?? 1);
        // circles: x/y is center
        const isCircle = obj.type === 'circle';
        switch (direction) {
          case 'left':    return { ...obj, x: isCircle ? (obj.radius ?? 0) * (obj.scaleX ?? 1) : 0 };
          case 'center-h': return { ...obj, x: isCircle ? cw / 2 : cw / 2 - w / 2 };
          case 'right':   return { ...obj, x: isCircle ? cw - (obj.radius ?? 0) * (obj.scaleX ?? 1) : cw - w };
          case 'top':     return { ...obj, y: isCircle ? (obj.radius ?? 0) * (obj.scaleY ?? 1) : 0 };
          case 'center-v': return { ...obj, y: isCircle ? ch / 2 : ch / 2 - h / 2 };
          case 'bottom':  return { ...obj, y: isCircle ? ch - (obj.radius ?? 0) * (obj.scaleY ?? 1) : ch - h };
          default: return obj;
        }
      })
    );
  };

  const toggleTextStyle = (style: 'bold' | 'italic' | 'underline') => {
    if (!selectedObjectId) return;
    
    setCanvasObjects(prev => prev.map(obj => {
      if (obj.id !== selectedObjectId || obj.type !== 'text') return obj;
      
      switch (style) {
        case 'bold':
          return {
            ...obj,
            fontStyle: obj.fontStyle?.includes('bold') ? obj.fontStyle.replace('bold', '').trim() : `${obj.fontStyle || ''} bold`.trim()
          };
        case 'italic':
          return {
            ...obj,
            fontStyle: obj.fontStyle?.includes('italic') ? obj.fontStyle.replace('italic', '').trim() : `${obj.fontStyle || ''} italic`.trim()
          };
        case 'underline':
          return {
            ...obj,
            textDecoration: obj.textDecoration === 'underline' ? undefined : 'underline'
          };
        default:
          return obj;
      }
    }));
  };

  // Add text formatting buttons to toolbar
  const TextFormatButtons = () => {
    if (!selectedObjectId) return null;
    const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
    if (!selectedObject || selectedObject.type !== 'text') return null;

    const btnBase = 'p-1.5 border rounded';
    const btnActive = 'border-blue-500 bg-blue-50 text-blue-700';
    const btnInactive = 'border-gray-300 hover:bg-gray-50';

    return (
      <div className="flex space-x-1">
        {/* Bold / Italic / Underline */}
        <button onClick={() => toggleTextStyle('bold')} className={`${btnBase} ${selectedObject.fontStyle?.includes('bold') ? btnActive : btnInactive}`} title="Bold">
          <span className="font-bold text-sm">B</span>
        </button>
        <button onClick={() => toggleTextStyle('italic')} className={`${btnBase} ${selectedObject.fontStyle?.includes('italic') ? btnActive : btnInactive}`} title="Italic">
          <span className="italic text-sm">I</span>
        </button>
        <button onClick={() => toggleTextStyle('underline')} className={`${btnBase} ${selectedObject.textDecoration === 'underline' ? btnActive : btnInactive}`} title="Underline">
          <span className="underline text-sm">U</span>
        </button>
        <div className="w-px bg-gray-300 mx-0.5" />
        {/* Text alignment */}
        <button onClick={() => changeTextAlign('left')} className={`${btnBase} ${(selectedObject.textAlign ?? 'left') === 'left' ? btnActive : btnInactive}`} title="Align Left">
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => changeTextAlign('center')} className={`${btnBase} ${selectedObject.textAlign === 'center' ? btnActive : btnInactive}`} title="Align Center">
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => changeTextAlign('right')} className={`${btnBase} ${selectedObject.textAlign === 'right' ? btnActive : btnInactive}`} title="Align Right">
          <AlignRight className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  const addArrow = () => {
    const stage = stageRef.current;
    if (!stage) return;

    const centerX = stage.width() / 2;
    const centerY = stage.height() / 2;
    
    const newArrow: CanvasObject = {
      id: generateId(),
      type: 'arrow',
      x: 0,
      y: 0,
      points: [centerX - 50, centerY, centerX + 50, centerY],
      fill: '#000000',
      stroke: '#000000',
      strokeWidth: 2,
      pointerLength: 20,
      pointerWidth: 20,
      tension: 0,
      draggable: true,
      scaleX: 1,
      scaleY: 1,
      rotation: 0
    };

    setCanvasObjects(prev => [...prev, newArrow]);
    setSelectedObjectId(newArrow.id);
  };

  const updateArrowTension = (tension: number) => {
    if (!selectedObjectId) return;
    
    setCanvasObjects(prev => prev.map(obj => {
      if (obj.id !== selectedObjectId || obj.type !== 'arrow') return obj;
      return { ...obj, tension };
    }));
  };

  // Add arrow button to toolbar
  const ArrowControls = () => {
    if (!selectedObjectId) return null;
    const selectedObject = canvasObjects.find(obj => obj.id === selectedObjectId);
    if (!selectedObject || selectedObject.type !== 'arrow') return null;

    return (
      <div className="flex items-center space-x-2">
        <span className="text-xs text-gray-600">Curve:</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={selectedObject.tension || 0}
          onChange={(e) => updateArrowTension(parseFloat(e.target.value))}
          className="w-20"
        />
      </div>
    );
  };

  // Update Stage content to include Arrow
  const renderObject = (obj: CanvasObject) => {
    const isSelected = selectedObjectIds.includes(obj.id);
    
    switch (obj.type) {
      case 'text':
        return (
          <EditableText
            key={obj.id}
            obj={obj}
            isSelected={isSelected}

            onSelect={(e) => handleObjectSelect(obj.id, e)}
            onEdit={(text) => handleTextEdit(obj.id, text)}
            onTransform={(attrs) => updateObjectTransform(obj.id, attrs)}
          />
        );
      case 'rectangle':
        return (
          <Rect
            key={obj.id}
            id={obj.id}
            x={obj.x}
            y={obj.y}
            width={obj.width}
            height={obj.height}
            fill={obj.fill}
            stroke={obj.stroke}
            strokeWidth={obj.strokeWidth}
            strokeScaleEnabled={false}
            cornerRadius={obj.cornerRadius}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation(),
              width: e.target.width(),
              height: e.target.height()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation(),
              width: e.target.width(),
              height: e.target.height()
            })}
          />
        );
      case 'circle':
        return (
          <Circle
            key={obj.id}
            id={obj.id}
            x={obj.x}
            y={obj.y}
            radius={obj.radius}
            fill={obj.fill}
            stroke={obj.stroke}
            strokeWidth={obj.strokeWidth}
            strokeScaleEnabled={false}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
          />
        );
      case 'qr-code':
        return (
          <VectorQRCode
            key={obj.id}
            obj={obj}
            isSelected={isSelected}
            onSelect={(e) => handleObjectSelect(obj.id, e)}
            onTransform={(attrs) => updateObjectTransform(obj.id, attrs)}
          />
        );
      case 'image':
        // Only render if image is properly loaded
        if (!obj.image || !obj.image.complete || obj.image.naturalWidth === 0) {
          console.log('Skipping image render - not loaded yet:', obj.id);
          return null;
        }
        return (
          <KonvaImage
            key={obj.id}
            id={obj.id}
            x={obj.x}
            y={obj.y}
            width={obj.width}
            height={obj.height}
            image={obj.image}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation(),
              width: e.target.width(),
              height: e.target.height()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation(),
              width: e.target.width(),
              height: e.target.height()
            })}
          />
        );
      case 'line':
        return (
          <Line
            key={obj.id}
            id={obj.id}
            points={obj.points || []}
            stroke={obj.stroke || obj.fill}
            strokeWidth={obj.strokeWidth}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
          />
        );
      case 'polygon':
        return (
          <Line
            key={obj.id}
            id={obj.id}
            points={obj.points || []}
            fill={obj.fill}
            closed={true}
            stroke={obj.stroke}
            strokeWidth={obj.strokeWidth}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
          />
        );
      case 'arrow':
        return (
          <Arrow
            key={obj.id}
            id={obj.id}
            points={obj.points || []}
            fill={obj.fill}
            stroke={obj.stroke}
            strokeWidth={obj.strokeWidth}
            pointerLength={obj.pointerLength}
            pointerWidth={obj.pointerWidth}
            tension={obj.tension}
            draggable={obj.draggable}
            scaleX={obj.scaleX}
            scaleY={obj.scaleY}
            rotation={obj.rotation}
            onClick={(e) => handleObjectSelect(obj.id, e)}
            onDragEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
            onTransformEnd={(e) => updateObjectTransform(obj.id, {
              x: e.target.x(),
              y: e.target.y(),
              scaleX: e.target.scaleX(),
              scaleY: e.target.scaleY(),
              rotation: e.target.rotation()
            })}
          />
        );
      default:
        return null;
    }
  };

  // Clear selection function
  const clearSelection = () => {
    setSelectedObjectIds([]);
    setSelectedObjectId(null);
  };

  // Add multi-select functionality
  const handleObjectSelect = (id: string, event?: any) => {
    const isMultiSelect = event && (event.evt?.shiftKey || event.evt?.metaKey || event.evt?.ctrlKey);

    if (isMultiSelect) {
      // Multi-select: add/remove from selection
      setSelectedObjectIds(prev => {
        if (prev.includes(id)) {
          // If object is already selected, remove it
          const newSelection = prev.filter(objId => objId !== id);
          // Update primary selection to the last remaining object
          setSelectedObjectId(newSelection.length > 0 ? newSelection[newSelection.length - 1] : null);
          return newSelection;
        } else {
          // If object is not selected, add it to selection
          const newSelection = [...prev, id];
          // Update primary selection to the newly added object
          setSelectedObjectId(id);
          return newSelection;
        }
      });
    } else {
      // Single select: check if clicking already selected object
      if (selectedObjectIds.length === 1 && selectedObjectIds[0] === id) {
        // If clicking the only selected object, keep it selected
        return;
      }
      // Otherwise, clear selection and select new object
      setSelectedObjectIds([id]);
      setSelectedObjectId(id);
    }
  };

  // Update transformer when selection changes
  useEffect(() => {
    if (transformerRef.current && stageRef.current) {
      const nodes = selectedObjectIds
        .map(objId => stageRef.current?.findOne(`#${objId}`))
        .filter(node => node !== undefined);
      transformerRef.current.nodes(nodes);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [selectedObjectIds]);

  // Add escape key handling
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Don't clear selection if we're editing text
        const isEditingText = document.querySelector('textarea[data-editing="true"]');
        if (!isEditingText) {
          clearSelection();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Sticker Designer</h1>
            <p className="text-sm text-gray-600">Design custom QR code stickers</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              placeholder="Design name..."
              className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="flex space-x-2">
              <button
                onClick={() => saveDesign()}
                disabled={saving || !designName.trim()}
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
              >
                <Save className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Save</span>
                <span className="sm:hidden">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal Toolbar */}
      {isTrialExpired && !isSubscribed && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Forms Deactivated - Trial Expired
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p className="mb-2">
                  Your trial has expired and all forms have been automatically deactivated. 
                  QR codes and form URLs are not accessible to the public.
                </p>
                <Link 
                  to="/dashboard/billing" 
                  className="font-medium text-red-800 underline hover:text-red-900"
                >
                  Subscribe to a plan
                </Link> to reactivate all forms immediately.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border-b border-gray-200 px-2 sm:px-4 py-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Form Selection */}
          <div className="w-full sm:w-auto">
            <select
              value={selectedForm}
              onChange={(e) => handleFormSelect(e.target.value)}
              className="w-full sm:w-auto px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select QR-Code</option>
              {forms.map((form) => (
                <option key={form.id} value={form.id}>
                  {form.name} {!form.is_active ? '(Inactive)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="hidden sm:block h-4 w-px bg-gray-300" />

          {/* Canvas Size Controls */}
          <div className="flex items-center space-x-2">
            <span className="text-xs text-gray-600">Size:</span>
            <div className="flex items-center space-x-1">
              <input
                type="number"
                value={getDisplayWidth()}
                onChange={(e) => handleWidthChange(parseFloat(e.target.value) || (measurementUnit === 'mm' ? 10 : 0.4))}
                min={measurementUnit === 'mm' ? "10" : "0.4"}
                max={measurementUnit === 'mm' ? "200" : "8"}
                step={measurementUnit === 'mm' ? "1" : "0.1"}
                className="w-14 px-1 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                title={`Width in ${measurementUnit}`}
              />
              <span className="text-xs text-gray-500">×</span>
              <input
                type="number"
                value={getDisplayHeight()}
                onChange={(e) => handleHeightChange(parseFloat(e.target.value) || (measurementUnit === 'mm' ? 10 : 0.4))}
                min={measurementUnit === 'mm' ? "10" : "0.4"}
                max={measurementUnit === 'mm' ? "200" : "8"}
                step={measurementUnit === 'mm' ? "1" : "0.1"}
                className="w-14 px-1 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                title={`Height in ${measurementUnit}`}
              />
              <select
                value={measurementUnit}
                onChange={(e) => setMeasurementUnit(e.target.value as 'mm' | 'inch')}
                className="px-1 py-1 border border-gray-300 rounded text-xs focus:ring-blue-500 focus:border-blue-500"
                title="Measurement Unit"
              >
                <option value="mm">mm</option>
                <option value="inch">in</option>
              </select>
            </div>
          </div>

          <div className="hidden sm:block h-4 w-px bg-gray-300" />

          {/* Format Selection */}
          <div className="flex space-x-1">
            <button
              onClick={() => {
                setSelectedFormat('circle');
                setCanvasWidthMm(50);
                setCanvasHeightMm(50);
              }}
              className={`p-1.5 rounded border ${
                selectedFormat === 'circle' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              title="Circle Format"
            >
              <CircleIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="hidden sm:block h-4 w-px bg-gray-300" />

          {/* Add Elements - Only Icons */}
          <div className="flex flex-wrap gap-1">
            <button
              onClick={addText}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Text"
            >
              <Type className="h-4 w-4" />
            </button>
            <button
              onClick={() => addShape('rectangle')}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Rectangle"
            >
              <Square className="h-4 w-4 rotate-45" />
            </button>
            <button
              onClick={() => addShape('square')}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Square"
            >
              <Square className="h-4 w-4" />
            </button>
            <button
              onClick={() => addShape('circle')}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Circle"
            >
              <CircleIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => addFullCanvasBackground()}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 relative"
              title={selectedObjectId ? "Resize Selected to Full Canvas" : "Add Full Canvas Background"}
            >
              <Square className="h-4 w-4 text-gray-400" />
              <div className="absolute inset-1 bg-gray-200 rounded-sm opacity-50"></div>
            </button>
            <button
              onClick={addImage}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Image"
            >
              <ImageIcon className="h-4 w-4" />
            </button>
            <button
              onClick={addArrow}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Add Arrow"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
            
            {/* Line Thickness Dropdown */}
            <div className="relative">
              <select
                onChange={(e) => addHorizontalLine(parseInt(e.target.value))}
                value=""
                className="p-1.5 border border-gray-300 rounded hover:bg-gray-50 text-sm"
                title="Add Horizontal Line"
              >
                <option value="" disabled>
                  ─
                </option>
                <option value="1">─ (1px)</option>
                <option value="2">━ (2px)</option>
                <option value="4">━ (4px)</option>
                <option value="6">━ (6px)</option>
                <option value="8">━ (8px)</option>
              </select>
            </div>
          </div>

          <div className="hidden sm:block h-4 w-px bg-gray-300" />

          {/* Text Formatting */}
          {selectedObject?.type === 'text' && <TextFormatButtons />}

          {/* Arrow Controls */}
          {selectedObject?.type === 'arrow' && <ArrowControls />}

          {/* Alignment Controls — canvas position */}
          {(selectedObjectId || selectedObjectIds.length > 0) && (
            <div className="flex space-x-1">
              {/* Horizontal position: only for non-text objects (text has its own L/C/R for within-box align) */}
              {selectedObject?.type !== 'text' && (
                <>
                  <button onClick={() => alignObjects('left')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Uitlijnen links op canvas">
                    <AlignLeft className="h-4 w-4" />
                  </button>
                  <button onClick={() => alignObjects('center-h')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Centreren horizontaal op canvas">
                    <AlignCenter className="h-4 w-4" />
                  </button>
                  <button onClick={() => alignObjects('right')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Uitlijnen rechts op canvas">
                    <AlignRight className="h-4 w-4" />
                  </button>
                </>
              )}
              {/* Vertical position: always shown */}
              <button onClick={() => alignObjects('top')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Uitlijnen boven op canvas">
                <AlignStartVertical className="h-4 w-4" />
              </button>
              <button onClick={() => alignObjects('center-v')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Centreren verticaal op canvas">
                <AlignCenterVertical className="h-4 w-4" />
              </button>
              <button onClick={() => alignObjects('bottom')} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50" title="Uitlijnen onder op canvas">
                <AlignEndVertical className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Group Controls */}
          <div className="flex space-x-1">
            <button
              onClick={groupSelected}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Group Selected (Ctrl/Cmd + G)"
              disabled={selectedObjectIds.length < 2}
            >
              <Square className="h-4 w-4" />
            </button>
            <button
              onClick={ungroupSelected}
              className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
              title="Ungroup Selected (Ctrl/Cmd + U)"
              disabled={!selectedObjectId || selectedObject?.type !== 'group'}
            >
              <Square className="h-4 w-4 rotate-45" />
            </button>
          </div>

          {/* Color Controls - Responsive */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Text Color (for text objects) */}
            {selectedObject?.type === 'text' && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-600 hidden sm:inline">Tekst:</span>
                <input
                  type="color"
                  value={selectedObject?.fill || '#000000'}
                  onChange={(e) => changeTextColor(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                  title="Tekstkleur"
                />
              </div>
            )}

            {/* Text box background color */}
            {selectedObject?.type === 'text' && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-600 hidden sm:inline">Achtergrond:</span>
                <input
                  type="color"
                  value={selectedObject.textBackground && selectedObject.textBackground !== 'transparent' ? selectedObject.textBackground : '#ffffff'}
                  onChange={(e) => changeTextBackground(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                  title="Achtergrondkleur tekstvak"
                />
                <button
                  onClick={() => changeTextBackground('transparent')}
                  className={`w-6 h-6 rounded border cursor-pointer flex items-center justify-center text-xs font-bold ${
                    !selectedObject.textBackground || selectedObject.textBackground === 'transparent'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Transparante achtergrond"
                >∅</button>
              </div>
            )}

            {/* Text box border color */}
            {selectedObject?.type === 'text' && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-600 hidden sm:inline">Rand:</span>
                <input
                  type="color"
                  value={selectedObject.textBorderColor && selectedObject.textBorderColor !== 'transparent' ? selectedObject.textBorderColor : '#000000'}
                  onChange={(e) => changeTextBorderColor(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                  title="Randkleur tekstvak"
                />
                <button
                  onClick={() => changeTextBorderColor('transparent')}
                  className={`w-6 h-6 rounded border cursor-pointer flex items-center justify-center text-xs font-bold ${
                    !selectedObject.textBorderColor || selectedObject.textBorderColor === 'transparent'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Geen rand"
                >∅</button>
                {selectedObject.textBorderColor && selectedObject.textBorderColor !== 'transparent' && (
                  <input
                    type="range"
                    min={1}
                    max={10}
                    value={selectedObject.textBorderWidth ?? 1}
                    onChange={(e) => changeTextBorderWidth(parseInt(e.target.value))}
                    className="w-14"
                    title="Randbreedte"
                  />
                )}
              </div>
            )}

            {/* Fill Color (for non-text objects) */}
            {selectedObject && selectedObject.type !== 'text' && selectedObject.type !== 'line' && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-600 hidden sm:inline">Fill:</span>
                <input
                  type="color"
                  value={selectedObject?.fill || '#3B82F6'}
                  onChange={(e) => changeObjectColor(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                  title="Fill Color"
                />
                <button
                  onClick={() => changeObjectColor('transparent')}
                  className={`w-6 h-6 rounded border border-gray-300 cursor-pointer flex items-center justify-center text-xs font-bold ${
                    selectedObject.fill?.startsWith('rgba') ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Transparent Fill"
                >
                  ∅
                </button>
              </div>
            )}

            {/* Stroke Color for shapes */}
            {selectedObject && (selectedObject.type === 'rectangle' || selectedObject.type === 'circle' || selectedObject.type === 'line') && (
              <div className="flex items-center space-x-1">
                <span className="text-xs text-gray-600 hidden sm:inline">Border:</span>
                <input
                  type="color"
                  value={selectedObject?.stroke || '#000000'}
                  onChange={(e) => changeStrokeColor(e.target.value)}
                  className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                  title="Border Color"
                />
              </div>
            )}

            {/* Canvas Background Color */}
            <div className="flex items-center space-x-1">
              <span className="text-xs text-gray-600 hidden sm:inline">Canvas:</span>
              <input
                type="color"
                value={canvasBackgroundColor}
                onChange={(e) => {
                  const newColor = e.target.value;
                  setCanvasBackgroundColor(newColor);
                  if (stageRef.current) stageRef.current.batchDraw();
                }}
                className="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                title="Canvas Background Color"
              />
            </div>
          </div>

          <div className="h-4 w-px bg-gray-300" />

          {/* Object Controls */}
          {selectedObject && (
            <>
              {/* Stroke Width */}
              {(selectedObject.type === 'rectangle' || selectedObject.type === 'circle' || selectedObject.type === 'line') && (
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600">Width:</span>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={selectedObject.strokeWidth || 2}
                    onChange={(e) => changeStrokeWidth(parseInt(e.target.value))}
                    className="w-12"
                    title="Border/Line Width"
                  />
                  <span className="text-xs text-gray-500 w-6">{selectedObject.strokeWidth || 2}</span>
                </div>
              )}

              {/* Corner Radius for Rectangles */}
              {selectedObject.type === 'rectangle' && (
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600">Radius:</span>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={selectedObject.cornerRadius || 0}
                    onChange={(e) => changeCornerRadius(parseInt(e.target.value))}
                    className="w-12"
                    title="Corner Radius"
                  />
                  <span className="text-xs text-gray-500 w-6">{selectedObject.cornerRadius || 0}</span>
                </div>
              )}

              {/* Font Controls for Text */}
              {selectedObject.type === 'text' && (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedObject.fontFamily || 'Arial'}
                    onChange={(e) => changeFontFamily(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    {fontOptions.map(font => (
                      <option key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-0.5">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      value={pxToPt(selectedObject.fontSize ?? ptToPx(10))}
                      onChange={(e) => changeFontSize(parseInt(e.target.value))}
                      className="w-14 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                      title="Lettergrootte in pt"
                    />
                    <span className="text-xs text-gray-500">pt</span>
                  </div>
                </div>
              )}

              {/* Object Actions */}
              <div className="flex space-x-1">
                <button
                  onClick={duplicateSelected}
                  className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
                  title="Duplicate"
                >
                  <Copy className="h-3 w-3" />
                </button>
                <button
                  onClick={bringToFront}
                  className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
                  title="Bring to Front"
                >
                  <ArrowUp className="h-3 w-3" />
                </button>
                <button
                  onClick={sendToBack}
                  className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"
                  title="Send to Back"
                >
                  <ArrowDown className="h-3 w-3" />
                </button>
                <button
                  onClick={deleteSelected}
                  className="p-1.5 border border-gray-300 rounded hover:bg-red-50 text-red-600"
                  title="Delete"
                >
                  <Trash className="h-3 w-3" />
                </button>
              </div>
            </>
          )}

          <div className="flex-1" />

          {/* View Controls */}
          <div className="flex space-x-1">
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`p-1.5 border rounded ${
                showGrid 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              title={showGrid ? 'Hide Grid' : 'Show Grid'}
            >
              {showGrid ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={() => {
                if (confirm('Are you sure you want to clear the entire canvas? This cannot be undone.')) {
                  setCanvasObjects([]);
                  setSelectedObjectId(null);
                  setSelectedObjectIds([]);
                }
              }}
              className="p-1.5 border border-gray-300 rounded hover:bg-red-50 text-red-600"
              title="Clear Canvas"
            >
              <Trash className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Alignment Toolbar removed per request */}

      {/* Export Controls */}
      <div className="bg-white border-b border-gray-200 px-2 sm:px-4 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Canvas Border Radius Control */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Canvas Radius:</label>
            <input
              type="range"
              min="0"
              max="50"
              value={canvasRadius}
              onChange={(e) => setCanvasRadius(parseInt(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600">{canvasRadius}mm</span>
              </div>

          <div className="h-4 w-px bg-gray-300" />

          {/* Export Format Selection */}
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Export as:</label>
                      <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'png' | 'pdf')}
              className="px-2 py-1 border border-gray-300 rounded text-sm"
            >
              <option value="png">PNG Image</option>
              <option value="pdf">PDF File</option>
                      </select>
                    </div>

          {exportFormat === 'png' && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Quality:</label>
                      <select
                value={exportQuality}
                onChange={(e) => setExportQuality(Number(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="1">Normal</option>
                <option value="2">High</option>
                <option value="3">Ultra</option>
                      </select>
                    </div>
          )}

          <button
            onClick={handleFileExport}
            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center space-x-1"
            disabled={!designName.trim()}
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>

          <button
            onClick={exportForPrint}
            disabled={exportingForPrint || !currentDesignId}
            className="px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!currentDesignId ? 'Sla het design eerst op' : 'Exporteer voor StickerIT print'}
          >
            <Printer className="h-4 w-4" />
            <span>{exportingForPrint ? 'Genereren...' : 'Export voor Print'}</span>
          </button>
                    </div>
                  </div>

      {/* Main Content Area */}
      <div className="flex-1 flex">
        {/* Canvas Area */}
        <div className="flex-1 p-4 overflow-auto">
          <div 
            className="mx-auto relative" 
            style={{ 
              borderRadius: `${canvasRadius}px`,
              overflow: 'hidden'
            }}
          >
            <Stage
              ref={stageRef}
              width={canvasDimensions.totalWidthPx}
              height={canvasDimensions.totalHeightPx}
              onClick={(e) => {
                // Clear selection when clicking on empty area or background
                if (e.target === e.target.getStage() || e.target.name() === 'background') {
                  clearSelection();
                }
              }}
            >
              <Layer>
                {/* Background Layer */}
                <Rect
                  name="background"
                  x={0}
                  y={0}
                  width={canvasDimensions.totalWidthPx}
                  height={canvasDimensions.totalHeightPx}
                  fill={canvasBackgroundColor}
                  cornerRadius={canvasRadius}
                />
                {/* Grid Layer */}
                {generateGridLines()}
                {/* Objects Layer */}
                {canvasObjects.map((obj, index) => {
                  console.log(`Rendering object ${index}:`, obj.type, obj.id, 'at', obj.x, obj.y, 'draggable:', obj.draggable);
                  return renderObject(obj);
                })}
                {/* Transformer Layer */}
                {(selectedObjectIds.length > 0) && (
                  <Transformer
                    ref={transformerRef}
                    boundBoxFunc={(_oldBox, newBox) => {
                      // Allow generous sizing for full canvas coverage
                      const minSize = 5;
                      const canvasWidth = canvasDimensions.totalWidthPx;
                      const canvasHeight = canvasDimensions.totalHeightPx;
                      
                      // Set very generous maximum limits to allow full canvas coverage
                      // Allow objects to be much larger than canvas for full coverage scenarios
                      const maxWidth = canvasWidth * 2; // Double canvas width
                      const maxHeight = canvasHeight * 2; // Double canvas height
                      
                      return {
                        ...newBox,
                        width: Math.max(minSize, Math.min(newBox.width, maxWidth)),
                        height: Math.max(minSize, Math.min(newBox.height, maxHeight))
                      };
                    }}
                    enabledAnchors={
                      selectedObject?.type === 'text'
                        ? ['middle-left', 'middle-right']
                        : ['top-left', 'top-center', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-center', 'bottom-right']
                    }
                    rotateEnabled={selectedObject?.type !== 'text'}
                    keepRatio={false}
                    padding={0}
                    anchorSize={10}
                    anchorCornerRadius={3}
                    borderStroke="#3B82F6"
                    anchorStroke="#2563EB"
                    anchorFill="#ffffff"
                    anchorStrokeWidth={2}
                  />
                )}
              </Layer>
            </Stage>
          </div>
        </div>
        
        {/* Saved Designs Panel */}
        <div className="w-64 bg-white border-l border-gray-200 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Saved Designs</h3>
            <div className="space-y-4">
              {loadingDesigns ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600">Loading designs...</p>
                </div>
              ) : Array.isArray(designs) && designs.length > 0 ? (
                designs.map((design) => (
                  <div
                    key={design.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => loadDesign(design)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{design.name}</h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDesign(design.id, design.name);
                        }}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                    {design.preview_url && (
                      <div className="relative aspect-square w-full overflow-hidden rounded-md bg-gray-100">
                        <img
                          src={design.preview_url}
                          alt={design.name}
                          className="object-contain w-full h-full"
                        />
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      {design.is_template ? 'Template' : 'Design'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600">No saved designs yet.</p>
                  <p className="text-xs text-gray-500 mt-1">Create and save a design to see it here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickerDesigner; 